/**
 * "¿Tu Instagram llena tu agenda?" — a 9-question scorecard.
 * Each answer scores 0–2, so the total runs from 0 to 18. The copy is a
 * starting draft for Jenifer to review; it makes no promise of results.
 */
type Copy = { es: string; en: string };

export type QuizArea = "perfil" | "contenido" | "conversion" | "medicion";

export type QuizQuestion = { id: string; area: QuizArea; text: Copy };

export const QUIZ_ANSWERS: readonly { score: number; label: Copy }[] = [
  { score: 2, label: { es: "Sí", en: "Yes" } },
  { score: 1, label: { es: "A medias", en: "Partly" } },
  { score: 0, label: { es: "No", en: "No" } },
];

export const QUIZ_QUESTIONS: readonly QuizQuestion[] = [
  {
    id: "bio",
    area: "perfil",
    text: {
      es: "¿Tu biografía dice en una frase qué hacés, para quién y cómo reservar?",
      en: "Does your bio say in one sentence what you do, who it's for and how to book?",
    },
  },
  {
    id: "highlights",
    area: "perfil",
    text: {
      es: "¿Tus historias destacadas guían a alguien nuevo hasta reservar (servicios, precios, opiniones, cómo llegar)?",
      en: "Do your story highlights guide a newcomer all the way to booking (services, prices, reviews, how to find you)?",
    },
  },
  {
    id: "link",
    area: "perfil",
    text: {
      es: "¿El enlace de tu perfil lleva directo a reservar o a escribirte?",
      en: "Does the link in your profile go straight to booking or to messaging you?",
    },
  },
  {
    id: "planning",
    area: "contenido",
    text: {
      es: "¿Planificás tus publicaciones con al menos una semana de antelación?",
      en: "Do you plan your posts at least a week ahead?",
    },
  },
  {
    id: "cta",
    area: "contenido",
    text: {
      es: "¿Tus publicaciones terminan con una llamada a la acción clara (reservá, escribime, guardá este post)?",
      en: "Do your posts end with a clear call to action (book, message me, save this post)?",
    },
  },
  {
    id: "proof",
    area: "contenido",
    text: {
      es: "¿Mostrás resultados reales de tu trabajo y opiniones de tus clientes?",
      en: "Do you show real results of your work and what your clients say?",
    },
  },
  {
    id: "booking",
    area: "conversion",
    text: {
      es: "¿Tus clientes pueden reservar sin esperar a que contestes un mensaje?",
      en: "Can your clients book without waiting for you to answer a message?",
    },
  },
  {
    id: "replies",
    area: "conversion",
    text: {
      es: "¿Respondés las consultas en menos de 2 horas, o tenés respuestas automáticas preparadas?",
      en: "Do you answer enquiries within 2 hours, or have automatic replies ready?",
    },
  },
  {
    id: "stats",
    area: "medicion",
    text: {
      es: "¿Revisás tus estadísticas cada mes y cambiás algo según lo que ves?",
      en: "Do you check your stats every month and change something based on what you see?",
    },
  },
];

export const QUIZ_MAX = QUIZ_QUESTIONS.length * 2;

export type QuizBand = { min: number; title: Copy; body: Copy; next: Copy };

/** Highest `min` first; the first band the score reaches wins. */
export const QUIZ_BANDS: readonly QuizBand[] = [
  {
    min: 14,
    title: { es: "Vas bien: ahora toca afinar", en: "You're doing well: now it's time to fine-tune" },
    body: {
      es: "Tu Instagram ya trabaja para tu negocio. El siguiente paso es medir mejor y automatizar para crecer sin sumar horas.",
      en: "Your Instagram already works for your business. The next step is measuring better and automating so you grow without adding hours.",
    },
    next: {
      es: "En la Auditoría gratuita te digo dónde está tu margen de mejora.",
      en: "In the free audit I'll show you where your room to improve is.",
    },
  },
  {
    min: 8,
    title: { es: "Tenés base, pero se te escapan reservas", en: "You have a base, but bookings are slipping away" },
    body: {
      es: "Hacés varias cosas bien, pero hay huecos entre que alguien te descubre y reserva. Ahí se pierden clientes.",
      en: "You do several things well, but there are gaps between someone discovering you and booking. That's where clients get lost.",
    },
    next: {
      es: "Pedí la Auditoría gratuita y te marco esos huecos. Si preferís hacerlo por tu cuenta, la Asesoría Estratégica te da el plan.",
      en: "Request the free audit and I'll point out those gaps. If you'd rather do it yourself, the Strategic Advisory gives you the plan.",
    },
  },
  {
    min: 0,
    title: { es: "Tu Instagram todavía no trabaja para tu agenda", en: "Your Instagram isn't working for your diary yet" },
    body: {
      es: "Hoy tu perfil depende de tu esfuerzo diario. Con una base clara (perfil, contenido con intención y un camino directo a reservar) el cambio se nota.",
      en: "Right now your profile depends on your daily effort. With a clear base (profile, purposeful content and a direct path to booking) the change shows.",
    },
    next: {
      es: "Empezá por la Auditoría gratuita para saber por dónde arrancar. Y si no tenés tiempo, puedo encargarme yo con la Gestión de Instagram.",
      en: "Start with the free audit to know where to begin. And if you don't have the time, I can take it on with Instagram Management.",
    },
  },
];

export const QUIZ_AREA_TIPS: Record<QuizArea, { name: Copy; tip: Copy }> = {
  perfil: {
    name: { es: "Perfil", en: "Profile" },
    tip: {
      es: "Es lo primero que ve alguien nuevo. Una bio clara, destacadas que guíen y un enlace directo a reservar suelen ser el cambio más rápido.",
      en: "It's the first thing a newcomer sees. A clear bio, guiding highlights and a direct booking link are usually the quickest win.",
    },
  },
  contenido: {
    name: { es: "Contenido", en: "Content" },
    tip: {
      es: "Publicar sin plan ni llamada a la acción genera me gusta, pero no reservas.",
      en: "Posting without a plan or a call to action earns likes, not bookings.",
    },
  },
  conversion: {
    name: { es: "Paso a la reserva", en: "Path to booking" },
    tip: {
      es: "Si reservar depende de que contestes un mensaje, se te escapan clientes fuera de horario.",
      en: "If booking depends on you answering a message, you lose clients outside working hours.",
    },
  },
  medicion: {
    name: { es: "Medición", en: "Measurement" },
    tip: {
      es: "Sin mirar los números es difícil saber qué contenido te trae clientes.",
      en: "Without looking at the numbers it's hard to know which content brings you clients.",
    },
  },
};

export const QUIZ_UI = {
  crumb: { es: "Test de Instagram", en: "Instagram quiz" },
  title: { es: "¿Tu Instagram llena tu agenda?", en: "Does your Instagram fill your diary?" },
  lead: {
    es: "9 preguntas, 2 minutos. Al final ves tu puntuación, tu punto más flojo y qué te conviene hacer primero.",
    en: "9 questions, 2 minutes. At the end you'll see your score, your weakest point and what to do first.",
  },
  start: { es: "Empezar el test", en: "Start the quiz" },
  question: { es: "Pregunta {current} de {total}", en: "Question {current} of {total}" },
  back: { es: "Atrás", en: "Back" },
  gateTitle: { es: "¿A dónde te mando tu resultado?", en: "Where should I send your result?" },
  gateBody: {
    es: "Te lo muestro ahora mismo y te dejo una copia en tu correo.",
    en: "I'll show it to you right now and send a copy to your inbox.",
  },
  name: { es: "Nombre", en: "Name" },
  email: { es: "Correo electrónico", en: "Email" },
  submit: { es: "Ver mi resultado", en: "See my result" },
  sending: { es: "Enviando…", en: "Sending…" },
  invalid: { es: "Revisá tu nombre y un correo válido.", en: "Check your name and a valid email." },
  privacyError: {
    es: "Aceptá la política de privacidad para ver tu resultado.",
    en: "Accept the privacy policy to see your result.",
  },
  privacySuffix: {
    es: "y autorizo que Lucentia Social me escriba sobre mi resultado.",
    en: "and allow Lucentia Social to email me about my result.",
  },
  marketing: {
    es: "Quiero recibir 5 correos con ideas para conseguir más reservas. Podés darte de baja cuando quieras.",
    en: "Send me 5 emails with ideas to get more bookings. You can unsubscribe at any time.",
  },
  scoreLabel: { es: "Tu puntuación", en: "Your score" },
  weakest: { es: "Tu punto más flojo", en: "Your weakest point" },
  disclaimer: {
    es: "Es una guía orientativa, no un diagnóstico completo. La auditoría mira tu cuenta de verdad.",
    en: "This is a rough guide, not a full diagnosis. The audit looks at your actual account.",
  },
  retake: { es: "Hacer el test de nuevo", en: "Take the quiz again" },
  whatsapp: {
    es: "Hola Jenifer, hice el test de Instagram y saqué {score}/{max}. Quiero la auditoría gratuita.",
    en: "Hi Jenifer, I took the Instagram quiz and scored {score}/{max}. I'd like the free audit.",
  },
} satisfies Record<string, Copy>;
